// Toast og hint. Toasten er en kort besked (fejl eller ros), hintet fortæller hvad man kan gøre nu.

import { announce } from './a11y';
import { h, setText, toggle } from './dom';
import { QUEUE_MS } from './messages';

/** Samme stærke ud-kurve som --ease-out i tokens.css. */
const EASE_OUT = 'cubic-bezier(0.23, 1, 0.32, 1)';

export type ToastKind = 'info' | 'warn' | 'good';

export interface ToastAction {
  label: string;
  run(): void;
}

interface Msg {
  text: string;
  kind: ToastKind;
  ms: number;
  avoid: { x: number; y: number } | null;
  action: ToastAction | null;
  /** 1 info, 2 advarsel, 3 svar på spillerens egen handling, 4 udfald (en by gik i sort). */
  prio: number;
  at: number;
  /** Beskeden gælder kun, så længe dette er sandt (fx stormen er stadig varslet). */
  valid: (() => boolean) | null;
}

/** Hvor længe en besked gælder: en tilstand i spillet. Holder den op, forsvinder beskeden (også i køen). */
export type ToastValid = (() => boolean) | null;

export class Toast {
  readonly el = h('div', { class: 'toast', hidden: true });
  readonly hint = h('p', { class: 'hint' });
  private timer = 0;
  private action: ToastAction | null = null;
  private hintTap: (() => void) | null = null;
  private cur: Msg | null = null;
  private next: Msg | null = null;
  private shownAt = 0;
  private forceTop = false;
  /** Udgangen på den sidst skjulte toast, mens den stadig fader (se ghost). */
  private fading: Animation | null = null;
  /** Noget vigtigere står fremme (messages.ts): toasten venter i køen imens. */
  private held = false;
  private static readonly MIN_MS = 1500;
  private static readonly STALE_MS = QUEUE_MS;
  /** En toast med en knap ("Flere pladser 10 kr") står længe nok til, at man kan nå den. */
  private static readonly ACTION_MS = 8000;
  /** ... og de første sekunder kan en almindelig besked ikke skubbe den væk (playtest: væk på 2-3 s). */
  private static readonly ACTION_GUARD_MS = 4500;
  /**
   * En systembesked (info eller advarsel) med samme tekst kommer højst én gang på 12 s. IGN runde 2:
   * "Vind og sol går i stå" og batteri-hintet kom igen og igen. Svar på spillerens egne handlinger
   * og udfald er undtaget: dem skal man altid have.
   */
  static readonly REPEAT_MS = 12_000;
  private lastShown = new Map<string, number>();

  constructor() {
    // En toast med en handling (fx "Opgradér station") kan trykkes på; det samme kan hintet,
    // når det peger på en bestemt ledning.
    this.el.addEventListener('pointerdown', (e) => e.stopPropagation());
    this.el.addEventListener('click', () => {
      const a = this.action;
      if (!a) return;
      this.clear();
      a.run();
    });
    this.el.addEventListener('keydown', (e) => {
      if (this.action && (e.key === 'Enter' || e.key === ' ')) {
        e.preventDefault();
        this.el.click();
      }
    });
    this.hint.addEventListener('pointerdown', (e) => e.stopPropagation());
    this.hint.addEventListener('click', () => this.hintTap?.());
  }

  /**
   * Toasten står nederst, hvor hintet står, og skjuler det imens, så kortets top og den samme besked
   * to gange ikke dækker for spillet. `avoid` er et punkt på skærmen (fx byen, det handler om):
   * dækker toasten det, flytter den op under HUD'en i stedet.
   *
   * Kun én besked ad gangen, og ingen blafren: en ny besked med samme eller lavere vigtighed venter,
   * til den nuværende har stået i mindst 1,5 s (en advarsel slår en info). Den vigtigste ventende
   * vises bagefter, hvis den stadig er frisk. Samme tekst igen forlænger bare den, der står.
   */
  show(text: string, kind: ToastKind = 'info', ms = 2400, avoid: { x: number; y: number } | null = null, action: ToastAction | null = null, valid: ToastValid = null): void {
    this.put({ text, kind, ms, avoid, action, prio: kind === 'warn' ? 2 : 1, at: performance.now(), valid });
  }

  /** Svar på noget, spilleren selv gjorde (fejl, fortryd, revet ned): vises altid med det samme. */
  say(text: string, kind: ToastKind = 'info', ms = 2400, avoid: { x: number; y: number } | null = null, action: ToastAction | null = null, valid: ToastValid = null): void {
    this.put({ text, kind, ms, avoid, action, prio: 3, at: performance.now(), valid });
  }

  /** Et udfald: går foran alt andet, også et åbent hændelseskort. */
  alarm(text: string, ms = 2600, avoid: { x: number; y: number } | null = null): void {
    this.put({ text, kind: 'warn', ms, avoid, action: null, prio: 4, at: performance.now(), valid: null });
  }

  /**
   * Hver frame: en besked, hvis tilstand er forbi, går væk, både den der står og den der venter.
   * IGN-løkke runde 2: "vil have strøm" og "på vej" blev stående, efter de var klaret.
   */
  prune(): void {
    if (this.next?.valid && !this.next.valid()) this.next = null;
    const c = this.el.hidden ? null : this.cur;
    if (c?.valid && !c.valid()) this.expire();
  }

  /** Prioriteten på den vigtigste besked, der står fremme eller venter frisk i køen (0 = ingen). */
  get wantPrio(): number {
    const cur = this.el.hidden ? 0 : (this.cur?.prio ?? 0);
    const n = this.next && performance.now() - this.next.at < Toast.STALE_MS ? this.next.prio : 0;
    return Math.max(cur, n);
  }

  /**
   * Hold toasten tilbage, mens noget vigtigere står fremme. Den, der stod, går tilbage i køen; nye
   * beskeder venter der også. Slippes den, kommer den vigtigste ventende frem, hvis den er under 6 s.
   */
  hold(on: boolean): void {
    if (on === this.held) return;
    this.held = on;
    if (on) {
      const c = this.el.hidden ? null : this.cur;
      // IGN-løkke 2, runde 2: "Ring til Norge!" kom fire gange under én vindstille, fordi Volts replikker
      // holdt toasten tilbage og slap den igen. En toast, der allerede har stået fremme i 1,5 s, er læst.
      const read = performance.now() - this.shownAt >= Toast.MIN_MS;
      if (c && !read && (!this.next || c.prio >= this.next.prio)) this.next = c;
      // Et fokuslag kommer frem: toasten går med det samme, ellers fadede den hen over bykortet.
      this.hide(false);
    } else {
      this.expire();
    }
  }

  /** Vises en toast lige nu? */
  get visible(): boolean {
    return !this.el.hidden;
  }

  private put(m: Msg): void {
    // Spor: tillid (IGN runde 4: en tom toast ""). En besked uden tekst vises aldrig.
    if (!m.text.trim()) return;
    if (this.held) {
      if (!this.next || m.prio >= this.next.prio) this.next = m;
      return;
    }
    const cur = this.el.hidden ? null : this.cur;
    if (m.prio <= 2 && (!cur || cur.text !== m.text) && m.at - (this.lastShown.get(m.text) ?? -Infinity) < Toast.REPEAT_MS) return;
    if (cur && cur.text === m.text) {
      this.arm(m.action ? Math.max(m.ms, Toast.ACTION_MS) : m.ms);
      return;
    }
    if (cur && cur.action && !m.action && m.prio < 4 && m.at - this.shownAt < Toast.ACTION_GUARD_MS) {
      if (!this.next || m.prio >= this.next.prio) this.next = m;
      return;
    }
    if (cur && m.prio < 3) {
      const age = m.at - this.shownAt;
      if (m.prio < cur.prio || (m.prio === cur.prio && age < Toast.MIN_MS)) {
        if (!this.next || m.prio >= this.next.prio) this.next = m;
        return;
      }
    }
    // Afløser en spillerbesked en frisk advarsel, kommer advarslen bagefter.
    if (cur && cur.prio === 2 && m.prio > cur.prio && m.at - this.shownAt < 1000 && (!this.next || this.next.prio <= 2)) this.next = cur;
    this.render(m);
  }

  private render(m: Msg): void {
    const { text, kind, ms, avoid, action } = m;
    // Står der allerede en, skifter den teksten på stedet med et lille skub i stedet for at blinke
    // ud og ind. Fra skjult kommer den ind via @starting-style (style.css).
    // En kopi, der stadig fader ud, betyder at den lige stod der: også et skift.
    const swap = !this.el.hidden || this.fading !== null;
    if (this.fading) {
      // Væk med det samme: en annulleret udgang ville ellers stå fuldt synlig til næste frame.
      (this.fading.effect as KeyframeEffect | null)?.target?.remove();
      this.fading.cancel();
      this.fading = null;
    }
    this.cur = m;
    this.shownAt = performance.now();
    this.lastShown.set(text, this.shownAt);
    if (this.lastShown.size > 64) {
      for (const [t, at] of this.lastShown) if (this.shownAt - at > Toast.REPEAT_MS) this.lastShown.delete(t);
    }
    setText(this.el, text);
    this.action = action;
    this.el.className = `toast ${kind}`;
    if (action) {
      this.el.append(h('span', { class: 'toast-act' }, action.label));
      this.el.classList.add('act');
      this.el.setAttribute('role', 'button');
      this.el.tabIndex = 0;
    } else {
      this.el.removeAttribute('role');
      this.el.removeAttribute('tabindex');
    }
    this.el.hidden = false;
    if (this.forceTop) this.el.classList.add('top');
    else if (avoid) {
      const r = this.el.getBoundingClientRect();
      const mg = 56;
      if (avoid.x > r.left - mg && avoid.x < r.right + mg && avoid.y > r.top - mg && avoid.y < r.bottom + mg) this.el.classList.add('top');
    }
    this.hint.classList.add('under');
    if (swap) this.nudge();
    this.arm(action ? Math.max(ms, Toast.ACTION_MS) : ms);
    announce(action ? `${text} ${action.label}` : text, kind === 'warn');
  }

  /** Hvornår den viste toast udløber, og hvor lang tid der var tilbage, da pausen frøs den. */
  private deadline = 0;
  private frozenLeft = 0;
  private frozen = false;

  private arm(ms: number): void {
    window.clearTimeout(this.timer);
    this.deadline = performance.now() + ms;
    if (this.frozen && this.cur?.action) {
      this.frozenLeft = ms;
      return;
    }
    this.timer = window.setTimeout(() => this.expire(), ms);
  }

  /**
   * Pause: en toast med en knap ("Vis", "Flere pladser") står, til spillet kører igen. IGN runde 1: den
   * forsvandt efter 8 s, mens spilleren holdt pause for at læse den.
   */
  freeze(on: boolean): void {
    if (on === this.frozen) return;
    this.frozen = on;
    if (on) {
      if (this.el.hidden || !this.cur?.action) return;
      window.clearTimeout(this.timer);
      this.frozenLeft = Math.max(Toast.MIN_MS, this.deadline - performance.now());
    } else if (this.frozenLeft > 0 && !this.el.hidden) {
      const left = this.frozenLeft;
      this.frozenLeft = 0;
      this.arm(left);
    } else this.frozenLeft = 0;
  }

  private expire(): void {
    const n = this.next;
    this.next = null;
    if (n && performance.now() - n.at < Toast.STALE_MS && (!n.valid || n.valid())) this.render(n);
    else this.hide();
  }

  /** Ny tekst i en toast, der allerede står: et kort skub opad, så skiftet kan ses. */
  private nudge(): void {
    if (typeof this.el.animate !== 'function') return;
    const calm = document.documentElement.classList.contains('calm');
    this.el.animate(calm ? [{ opacity: 0.4 }, { opacity: 1 }] : [{ translate: '0 4px', opacity: 0.6 }, { translate: '0 0', opacity: 1 }], {
      duration: 160,
      easing: EASE_OUT,
    });
  }

  /**
   * Ud: en stum kopi fader væk på 140 ms, mens selve toasten er skjult med det samme. Så kan en ny
   * besked komme frem uden at vente, og tests og skærmlæsere ser kun den rigtige.
   */
  private ghost(): void {
    if (this.el.hidden || !this.el.isConnected || typeof this.el.animate !== 'function') return;
    const g = this.el.cloneNode(true) as HTMLElement;
    g.classList.add('toast-ghost');
    g.removeAttribute('role');
    g.removeAttribute('tabindex');
    g.setAttribute('aria-hidden', 'true');
    this.el.after(g);
    const calm = document.documentElement.classList.contains('calm');
    const a = g.animate(calm ? [{ opacity: 1 }, { opacity: 0 }] : [{ opacity: 1 }, { opacity: 0, translate: '0 4px', scale: '0.98' }], {
      duration: 140,
      easing: EASE_OUT,
      fill: 'forwards',
    });
    this.fading = a;
    a.onfinish = a.oncancel = () => {
      g.remove();
      if (this.fading === a) this.fading = null;
      // Hintet kommer først frem, når kopien er væk: ellers lå de oven i hinanden et øjeblik (spor "coop").
      if (this.el.hidden && !this.fading) this.hint.classList.remove('under');
    };
  }

  private hide(fade = true): void {
    window.clearTimeout(this.timer);
    if (fade) this.ghost();
    else this.fading?.cancel();
    this.el.hidden = true;
    this.action = null;
    this.cur = null;
    if (!this.fading) this.hint.classList.remove('under');
  }

  /** Fjern netop denne besked, hvis den står fremme eller venter: den passer ikke længere. */
  drop(text: string): void {
    if (this.next?.text === text) this.next = null;
    if (!this.el.hidden && this.cur?.text === text) this.hide();
  }

  /** Fjern en toast, der ikke længere passer (fx efter skift af værktøj). Ventende beskeder droppes også. */
  clear(): void {
    this.next = null;
    this.hide();
  }

  /**
   * Mens noget nederst på skærmen skal kunne ses (fx byvælgeren til nødaflastning), står toasten
   * altid oppe under HUD'en.
   */
  setTop(on: boolean): void {
    this.forceTop = on;
    if (on && !this.el.hidden) this.el.classList.add('top');
  }

  /** Hintet nederst. Med `onTap` kan det trykkes på (fx for at åbne menuen for ledningen, det nævner). */
  setHint(text: string, urgent = false, onTap: (() => void) | null = null): void {
    setText(this.hint, text);
    toggle(this.hint, 'urgent', urgent);
    toggle(this.hint, 'tap', onTap !== null);
    this.hintTap = onTap;
    this.hint.hidden = text === '';
  }
}
